import { api, unwrap } from "@lumberset/api-client";
import {
  ChangeOrderStatus,
  DeltaSummary,
  DeltaTable,
  dateTime,
  money,
} from "@lumberset/takeoff-ui";
import {
  Button,
  Container,
  EmptyState,
  Prompt,
  SectionHeader,
  SectionRow,
  toast,
} from "@lumberset/ui";
import { ArrowDownTray, CreditCard, DocumentText, ExclamationCircle } from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  AddCardDialog,
  type CardPurpose,
  cardNeeded,
  creditFeeLine,
  refusedStart,
  useCardReturn,
} from "../../../../components/add-card";
import { Crumbs } from "../../../../components/crumbs";
import { TakeoffProgress } from "../../../../components/takeoff-progress";
import { billingSummaryQuery, changeOrderQuery, projectQuery } from "../../../../lib/queries";
import { UploadRevisedDrawings } from "./-upload-revised-drawings";

interface ChangeOrderSearch {
  /** Run the takeoff once a card is on file. */
  resume?: "run";
  /** Back from Stripe's card page. */
  card?: "saved" | "canceled";
}

export const Route = createFileRoute("/_app/projects/$id/change-orders/$coId")({
  validateSearch: (search: Record<string, unknown>): ChangeOrderSearch => ({
    ...(search.resume === "run" ? { resume: "run" as const } : {}),
    ...(search.card === "saved" || search.card === "canceled" ? { card: search.card } : {}),
  }),
  loader: ({ context: { queryClient }, params }) =>
    Promise.all([
      queryClient.ensureQueryData(projectQuery(params.id)),
      queryClient.ensureQueryData(changeOrderQuery(params.coId)),
    ]),
  component: ChangeOrderScreen,
});

const working = new Set(["running", "in_review"]);

function ChangeOrderScreen() {
  const { id, coId } = Route.useParams();
  const { yard, user } = Route.useRouteContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const builder = yard.kind === "builder";
  const [canceling, setCanceling] = useState(false);

  const { data: parent } = useSuspenseQuery(projectQuery(id));
  const { data } = useSuspenseQuery({
    ...changeOrderQuery(coId),
    refetchInterval: (query) =>
      working.has(query.state.data?.changeOrder.status ?? "") ? 3000 : false,
  });
  const { data: billing } = useQuery(billingSummaryQuery);
  const { changeOrder, run, planSet, delta } = data;

  const fee = billing?.summary.nextChangeOrder;
  const feeCents = fee?.amountCents;
  const label = `Change Order ${changeOrder.number}`;

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ["change-orders"] });
    await queryClient.invalidateQueries({ queryKey: ["projects", id] });
  };

  const runPurpose: CardPurpose = {
    kind: "run_change_order",
    amountCents: feeCents ?? 0,
    projectId: id,
    changeOrderId: coId,
  };
  const [cardFor, setCardFor] = useState<CardPurpose | null>(null);
  const runTakeoff = useMutation({
    mutationFn: (_afterCard: boolean) =>
      unwrap(api["change-orders"][":id"].run.$post({ param: { id: coId } })),
    onSuccess: async (_result, afterCard) => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: ["billing"] });
      toast.success(afterCard ? "Card saved. Reading has started." : "Reading has started.");
    },
    onError: (error) => {
      // Refused for want of a card: ask for one, never an error toast.
      const card = refusedStart(error, runPurpose);
      if (card) setCardFor(card);
      else toast.error(error.message);
    },
  });

  /** Runs the takeoff, or first asks for the card the change order will be charged to. */
  function startReading() {
    if (cardNeeded(billing?.summary, "change_order")) setCardFor(runPurpose);
    else runTakeoff.mutate(false);
  }

  const cancel = useMutation({
    mutationFn: () => unwrap(api["change-orders"][":id"].cancel.$post({ param: { id: coId } })),
    onSuccess: async () => {
      await invalidate();
      setCanceling(false);
      toast.success(`${label} canceled. Nothing was charged.`);
    },
    onError: (error) => toast.error(error.message),
  });

  const createQuote = useMutation({
    mutationFn: (input: { runId: string; scope: "full" | "changes" }) =>
      unwrap(api.quotes.$post({ json: input })),
    onSuccess: async ({ quote, matched, lineCount }) => {
      await queryClient.invalidateQueries({ queryKey: ["quotes"] });
      await queryClient.invalidateQueries({ queryKey: ["projects", id] });
      toast.success(`Quote #${quote.number} drafted`, {
        description: `${matched} of ${lineCount} lines matched to your catalog.`,
      });
      await navigate({ to: "/quotes/$id", params: { id: quote.id } });
    },
    onError: (error) => toast.error(error.message),
  });

  const draft = changeOrder.status === "draft";
  // Drawings uploaded and closed, but no run in flight: after a failed run, or a refresh mid-flow.
  const readyToRun = draft && planSet !== null && planSet.status !== "uploading";
  const released = changeOrder.status === "released" && run !== null;

  /* ---- Picking up after the card ------------------------------------------- */

  const search = Route.useSearch();
  const clearResume = () =>
    navigate({
      to: "/projects/$id/change-orders/$coId",
      params: { id, coId },
      search: {},
      replace: true,
    });

  // Back from Stripe: wait until the card shows (the webhook can lag), then run.
  const cardReturn = useCardReturn({
    active: search.resume === "run" && search.card !== undefined,
    card: search.card,
    onReady: () => {
      void clearResume();
      if (readyToRun) runTakeoff.mutate(true);
    },
    onCanceled: () => {
      void clearResume();
      toast.info("No card was added", {
        description: "The revised drawings are saved. Run the takeoff when you are ready.",
      });
    },
  });

  // From the project's uploader or the owners' email link: ask for the card, or run when there already is one.
  const pickedUp = useRef(false);
  useEffect(() => {
    if (search.resume !== "run" || search.card !== undefined || !billing || pickedUp.current) {
      return;
    }
    pickedUp.current = true;
    if (!readyToRun) {
      void clearResume();
      return;
    }
    if (cardNeeded(billing.summary, "change_order")) {
      setCardFor(runPurpose);
      return;
    }
    void clearResume();
    runTakeoff.mutate(false);
  });

  return (
    <>
      <Crumbs
        trail={[
          { label: "Projects", to: "/projects" },
          { label: parent.project.name, to: "/projects/$id", params: { id } },
          { label },
        ]}
      />

      {cardReturn.waiting ? (
        <Container className="flex items-start gap-x-3 px-6 py-4">
          <span className="mt-0.5 text-ui-fg-subtle">
            <CreditCard />
          </span>
          <div className="min-w-0">
            <p className="txt-compact-small-plus text-ui-fg-base">
              {cardReturn.timedOut ? "Still waiting for your card" : "Saving your card"}
            </p>
            <p className="txt-small mt-0.5 text-pretty text-ui-fg-subtle">
              {cardReturn.timedOut
                ? "It can take a minute to come through from Stripe. This page keeps checking, and runs the takeoff by itself once it does."
                : "This takes a few seconds. Then the takeoff runs by itself."}
            </p>
          </div>
        </Container>
      ) : null}

      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title={`${label}: ${changeOrder.title}`}
          description={
            changeOrder.releasedAt
              ? `Released ${dateTime(changeOrder.releasedAt)}`
              : `Opened ${dateTime(changeOrder.createdAt)}`
          }
          actions={
            <>
              <ChangeOrderStatus status={changeOrder.status} />
              {draft ? (
                <Button size="small" variant="secondary" onClick={() => setCanceling(true)}>
                  Cancel
                </Button>
              ) : null}
            </>
          }
        />
        {changeOrder.description ? (
          <SectionRow label="What changed">{changeOrder.description}</SectionRow>
        ) : null}
        {changeOrder.feeCents !== null ? (
          <SectionRow label="Change order fee">
            {changeOrder.feeCents === 0 ? "No charge" : money(changeOrder.feeCents)}
          </SectionRow>
        ) : null}
        {released ? (
          // Its own row, so the actions wrap on a phone instead of pushing the page sideways.
          <div className="flex flex-wrap items-center gap-2 px-6 py-4">
            {builder ? (
              <Button size="small" asChild>
                <Link to="/projects/$id/takeoffs/$runId" params={{ id, runId: run.id }}>
                  Budget
                </Link>
              </Button>
            ) : (
              <>
                <Button
                  size="small"
                  isLoading={createQuote.isPending && createQuote.variables?.scope === "changes"}
                  disabled={createQuote.isPending}
                  onClick={() => createQuote.mutate({ runId: run.id, scope: "changes" })}
                >
                  Quote the changes
                </Button>
                <Button
                  size="small"
                  variant="secondary"
                  isLoading={createQuote.isPending && createQuote.variables?.scope === "full"}
                  disabled={createQuote.isPending}
                  onClick={() => createQuote.mutate({ runId: run.id, scope: "full" })}
                >
                  Quote the full revised takeoff
                </Button>
              </>
            )}
            <Button size="small" variant="secondary" asChild>
              <Link to="/projects/$id/takeoffs/$runId" params={{ id, runId: run.id }}>
                <DocumentText /> Open the takeoff
              </Link>
            </Button>
          </div>
        ) : null}
        {released && delta ? <DeltaSummary delta={delta} /> : null}
      </Container>

      {draft && !readyToRun ? (
        <Container className="divide-y p-0">
          <SectionHeader
            title="Revised drawings"
            description="Upload the new issue of the plan set. We read it and show you what changed against the released takeoff."
          />
          {run?.status === "failed" ? (
            <div className="txt-small flex items-start gap-x-2 bg-ui-tag-red-bg px-6 py-3 text-ui-tag-red-text">
              <ExclamationCircle className="mt-0.5 shrink-0" />
              <p>
                {run.error ?? "Something stopped the last takeoff."} You were not charged. Upload
                the drawings again to retry.
              </p>
            </div>
          ) : null}
          <UploadRevisedDrawings
            projectId={id}
            changeOrderId={coId}
            fee={fee}
            onUploaded={startReading}
          />
        </Container>
      ) : null}

      {readyToRun ? (
        <Container className="divide-y p-0">
          <SectionHeader
            title="Revised drawings"
            description={`${planSet.label}, ${planSet.sheetCount > 0 ? `${planSet.sheetCount} ${planSet.sheetCount === 1 ? "sheet" : "sheets"}` : "uploaded"}.`}
          />
          {run?.status === "failed" ? (
            <div className="txt-small flex items-start gap-x-2 bg-ui-tag-red-bg px-6 py-3 text-ui-tag-red-text">
              <ExclamationCircle className="mt-0.5 shrink-0" />
              <p>{run.error ?? "Something stopped the last takeoff."} You were not charged.</p>
            </div>
          ) : null}
          <div className="flex flex-col gap-3 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="txt-small text-ui-fg-subtle">
              {fee === undefined || feeCents === undefined
                ? "Read the revised drawings."
                : (creditFeeLine(fee, "change order", false) ??
                  (feeCents === 0
                    ? "Change orders are free on your billing plan. Nothing to pay."
                    : `${money(feeCents)} when the change order is released to you. Nothing if it fails.`))}
            </p>
            <Button
              className="max-sm:w-full"
              isLoading={runTakeoff.isPending}
              onClick={startReading}
            >
              Run takeoff
            </Button>
          </div>
        </Container>
      ) : null}

      {run && working.has(changeOrder.status) ? (
        <TakeoffProgress
          runId={run.id}
          stage={run.stage}
          progress={run.progress}
          status={changeOrder.status}
          gated={run.gated}
          heldFor={run.heldFor}
          what="change order"
          email={user.email}
        />
      ) : null}

      {changeOrder.status === "canceled" ? (
        <Container className="p-0">
          <EmptyState
            title="This change order was canceled"
            description="Nothing was charged for it. Start a new change order from the project when the revised drawings are ready."
          />
        </Container>
      ) : null}

      {released ? (
        <Container className="divide-y overflow-hidden p-0">
          <SectionHeader
            title="What changed"
            description="Every material whose quantity differs from the released takeoff, added up across the building. Added lines are green, removed lines are red."
            actions={
              delta ? (
                <Button size="small" variant="secondary" asChild>
                  <a href={`/api/v1/change-orders/${coId}/delta.csv`} download>
                    <ArrowDownTray /> Download CSV
                  </a>
                </Button>
              ) : null
            }
          />
          {delta ? (
            <DeltaTable delta={delta} />
          ) : (
            <EmptyState
              className="py-10"
              title="No comparison yet"
              description="The takeoff this change order revises has no lines to compare against."
            />
          )}
        </Container>
      ) : null}

      {cardFor ? (
        <AddCardDialog
          open
          onOpenChange={(open) => {
            if (open) return;
            setCardFor(null);
            if (search.resume) void clearResume();
          }}
          purpose={cardFor}
          returnTo={`/projects/${id}/change-orders/${coId}?resume=run`}
        />
      ) : null}

      <Prompt open={canceling} onOpenChange={setCanceling}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Cancel {label}?</Prompt.Title>
            <Prompt.Description>
              Nothing has been read yet, so nothing is charged. You can open a new change order
              later.
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Keep it</Prompt.Cancel>
            <Button size="small" isLoading={cancel.isPending} onClick={() => cancel.mutate()}>
              Cancel change order
            </Button>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </>
  );
}
